import { useMemo, useState } from 'react';
import type { RatesSettings } from '@/settings/types';
import { CEB_REV_2026_DEFAULTS } from '@/settings/defaults';
import { formatLkr } from '@/lib/format';

interface Props {
  initial: RatesSettings;
  onSave: (rates: RatesSettings) => Promise<void>;
}

export function RatesEditor({ initial, onSave }: Props) {
  const [draft, setDraft] = useState<RatesSettings>(() => structuredClone(initial));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initial), [draft, initial]);

  const set = <K extends keyof RatesSettings>(key: K, value: RatesSettings[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const updateBlock = (
    catIdx: number,
    blockIdx: number,
    patch: Partial<RatesSettings['tariff']['categories'][number]['blocks'][number]>,
  ) => {
    setDraft((d) => {
      const next = structuredClone(d);
      Object.assign(next.tariff.categories[catIdx].blocks[blockIdx], patch);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await onSave(draft);
      setMessage({ kind: 'ok', text: 'Rates saved.' });
    } catch (err) {
      setMessage({ kind: 'err', text: (err as Error).message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setDraft(structuredClone(CEB_REV_2026_DEFAULTS));
    setMessage({ kind: 'ok', text: 'Reverted form to CEB Rev-2026 defaults. Click Save to persist.' });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm text-slate-700 mb-1">Tariff revision label</label>
        <input
          value={draft.tariff.revisionLabel}
          onChange={(e) =>
            set('tariff', { ...draft.tariff, revisionLabel: e.target.value })
          }
          className="w-full rounded-md border border-slate-300 px-3 py-2"
        />
      </div>

      {draft.tariff.categories.map((cat, catIdx) => (
        <div key={cat.id} className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Category {cat.id}</div>
              <div className="text-xs text-slate-500">{cat.label}</div>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left font-medium px-2 py-1">From (kWh)</th>
                <th className="text-left font-medium px-2 py-1">To (kWh)</th>
                <th className="text-left font-medium px-2 py-1">Energy (Rs./kWh)</th>
                <th className="text-left font-medium px-2 py-1">Fixed (Rs.)</th>
              </tr>
            </thead>
            <tbody>
              {cat.blocks.map((block, blockIdx) => (
                <tr key={blockIdx} className="border-t border-slate-100">
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      value={block.fromUnits}
                      onChange={(e) =>
                        updateBlock(catIdx, blockIdx, { fromUnits: Number(e.target.value) })
                      }
                      className="w-24 rounded border border-slate-300 px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      value={block.toUnits ?? ''}
                      placeholder="∞"
                      onChange={(e) =>
                        updateBlock(catIdx, blockIdx, {
                          toUnits: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      className="w-24 rounded border border-slate-300 px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      step="0.01"
                      value={block.energyRateLkr}
                      onChange={(e) =>
                        updateBlock(catIdx, blockIdx, { energyRateLkr: Number(e.target.value) })
                      }
                      className="w-28 rounded border border-slate-300 px-2 py-1"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      step="0.01"
                      value={block.fixedChargeLkr ?? ''}
                      placeholder="N/A"
                      onChange={(e) =>
                        updateBlock(catIdx, blockIdx, {
                          fixedChargeLkr: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      className="w-28 rounded border border-slate-300 px-2 py-1"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <div className="rounded-lg border border-slate-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-slate-700 mb-1">Normal fan power (W)</label>
          <input
            type="number"
            value={draft.fans.normalWatts}
            onChange={(e) => set('fans', { ...draft.fans, normalWatts: Number(e.target.value) })}
            className="w-full rounded-md border border-slate-300 px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm text-slate-700 mb-1">Atomberg fan power (W)</label>
          <input
            type="number"
            value={draft.fans.atombergWatts}
            onChange={(e) =>
              set('fans', { ...draft.fans, atombergWatts: Number(e.target.value) })
            }
            className="w-full rounded-md border border-slate-300 px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm text-slate-700 mb-1">Default hours per day</label>
          <input
            type="number"
            value={draft.fans.defaultHoursPerDay}
            onChange={(e) =>
              set('fans', { ...draft.fans, defaultHoursPerDay: Number(e.target.value) })
            }
            className="w-full rounded-md border border-slate-300 px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm text-slate-700 mb-1">Effective-fans divisor</label>
          <input
            type="number"
            step="0.1"
            value={draft.fans.effectiveFansDivisor}
            onChange={(e) =>
              set('fans', { ...draft.fans, effectiveFansDivisor: Number(e.target.value) })
            }
            className="w-full rounded-md border border-slate-300 px-3 py-2"
          />
          <p className="text-xs text-slate-500 mt-1">
            "Half the fans run continuously" → divisor = 2.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-900 mb-1">SSCL tax</div>
        <p className="text-xs text-slate-500 mb-3">
          Social Security Contribution Levy — inclusive of the final bill. Formula:
          SSCL = subtotal × rate / (100 − rate). Set 0 to disable.
        </p>
        <label className="block text-sm text-slate-700 mb-1">Rate (%)</label>
        <input
          type="number"
          step="0.01"
          min={0}
          max={100}
          value={draft.sscl.ratePercent}
          onChange={(e) => set('sscl', { ratePercent: Number(e.target.value) })}
          className="w-full sm:w-40 rounded-md border border-slate-300 px-3 py-2"
        />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm text-slate-700 mb-1">Electrician (one-time, Rs.)</label>
          <input
            type="number"
            value={draft.maintenance.electricianLkr}
            onChange={(e) =>
              set('maintenance', { ...draft.maintenance, electricianLkr: Number(e.target.value) })
            }
            className="w-full rounded-md border border-slate-300 px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm text-slate-700 mb-1">Condenser (per cycle, Rs.)</label>
          <input
            type="number"
            value={draft.maintenance.condenserLkr}
            onChange={(e) =>
              set('maintenance', { ...draft.maintenance, condenserLkr: Number(e.target.value) })
            }
            className="w-full rounded-md border border-slate-300 px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm text-slate-700 mb-1">Cycle length (months)</label>
          <input
            type="number"
            value={draft.maintenance.cycleMonths}
            onChange={(e) =>
              set('maintenance', { ...draft.maintenance, cycleMonths: Number(e.target.value) })
            }
            className="w-full rounded-md border border-slate-300 px-3 py-2"
          />
        </div>
        <div className="sm:col-span-3 text-xs text-slate-500">
          Preview: 4 fans, 3 years → maintenance saving{' '}
          <span className="font-medium text-slate-700">
            {formatLkr(
              draft.maintenance.electricianLkr * 4 +
                draft.maintenance.condenserLkr *
                  4 *
                  Math.floor((3 * 12) / draft.maintenance.cycleMonths),
            )}
          </span>
        </div>
      </div>

      {message && (
        <div
          className={`text-sm rounded-md px-3 py-2 border ${
            message.kind === 'ok'
              ? 'text-emerald-800 bg-emerald-50 border-emerald-200'
              : 'text-red-700 bg-red-50 border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="px-4 py-2 rounded-md bg-brand-500 text-slate-900 font-medium hover:bg-brand-600 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={resetToDefaults}
          className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
        >
          Reset form to CEB Rev-2026 defaults
        </button>
        {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
      </div>
    </div>
  );
}
